import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, CircularProgress, Button,
  TextField, MenuItem, Select, FormControl, Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import LockResetIcon from "@mui/icons-material/LockReset";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import moment from "moment";
import {
  get_school_student_detail_api,
  update_school_student_api,
  send_student_password_reset_api,
  resend_student_invite_api,
  delete_school_student_api,
  generate_student_mistake_briefing_api,
  get_student_record_api,
  update_student_task_api,
  approve_session_summary_api,
} from "../../DAL/SchoolPortal/SchoolPortal";
import RecordHeader from "./StudentRecord/RecordHeader";
import RecordWork from "./StudentRecord/RecordWork";
import RecordGetStarted from "./StudentRecord/RecordGetStarted";
import RecordSubjects from "./StudentRecord/RecordSubjects";
import RecordExamPicture from "./StudentRecord/RecordExamPicture";
import RecordActivity from "./StudentRecord/RecordActivity";
import RecordBriefing from "./StudentRecord/RecordBriefing";
import { act_on_coaching_api } from "../../DAL/SchoolPortal/SchoolPortal";
import StudentAIChat from "../../components/StudentAIChat";
import DigIn from "./StudentRecord/DigIn";
import RecordTimeline from "./StudentRecord/RecordTimeline";
import RecordOpenItems from "./StudentRecord/RecordOpenItems";
import RecordActionPanel from "./StudentRecord/RecordActionPanel";
import HomeworkModal from "./StudentRecord/HomeworkModal";
import { kindMeta } from "./StudentRecord/recordTheme";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, inputSx,
  primaryButtonSx, secondaryButtonSx, error as errorColor, easing,
} from "../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The student record.
 *
 * WHAT THIS PAGE IS FOR
 * Five questions an instructor asks out loud, in this order:
 *   how are they doing with X · are they ready · how many questions have they
 *   done · when did they last see this · what kind of questions are they getting
 * Everything on screen answers one of those. Everything that does not is shut.
 *
 * WHAT WAS REMOVED, AND WHY
 * A three-column grid with ten blocks: course progress, a risk band, written
 * readiness, revenue, a weakest-areas list, a nine-row dotted checklist, a
 * nine-type audit log, an offers counter, a subject-mastery grid and an AI
 * chat. Between them they showed a dozen derived numbers and answered none of
 * the five questions — the dotted checklist in particular gave a colour per
 * subject and no counts, so "412 questions in air law and none in navigation"
 * appeared nowhere. The grid also made an instructor choose a column before
 * reading anything.
 *
 * WHAT IS LEFT OPEN
 *   identity      who, when they last WORKED, next lesson
 *   what to cover 1-3 topics with the wrong answer they keep giving, each
 *                 with the action that messages them about it
 *   how doing     subject -> topic -> the actual questions, as counts
 * Open items, history and the profile are real, and are one click away.
 *
 * ONE FETCH, ONE SHAPE
 * GET /api/school/students/:id/record, assembled in studentRecordService.
 * The client renders it; it does not merge collections.
 */

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "suspended", label: "Suspended" },
  { value: "expired", label: "Expired" },
];

const fieldRowSx = { display: "flex", alignItems: "center", gap: 1, py: 0.4 };

/**
 * A shut section. Everything that is real but is not the reason anyone opened
 * this page lives in one of these — closed, labelled, and carrying its own
 * count so nothing has to be opened to find out whether it is empty.
 */
function Drawer({ title, count = null, subtitle = null, defaultOpen = false, tone = "card", children }) {
  const [open, setOpen] = useState(defaultOpen);
  // TWO TONES, BECAUSE THESE ARE TWO DIFFERENT KINDS OF THING.
  //
  // A white card says "an object worth your attention". Six of them stacked
  // says nothing at all, which is what this page had: header, brief, table and
  // three drawers, every one of them white with the same border on a near-white
  // ground. Open items is work waiting on you and keeps the card. History and
  // the profile are an archive — they sit flat on the page, and the difference
  // is visible before a single word is read.
  const quiet = tone === "quiet";
  return (
    <Box sx={{
      backgroundColor: quiet ? "transparent" : "#fff",
      border: quiet ? "none" : `1px solid ${border}`,
      borderTop: quiet ? `1px solid ${border}` : undefined,
      borderRadius: quiet ? 0 : "10px",
      mb: quiet ? 0 : 1,
      overflow: "hidden",
    }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: quiet ? 0.25 : 1.75, py: 1.25, cursor: "pointer",
          "&:hover": { backgroundColor: quiet ? "transparent" : "#FAF8F5", opacity: quiet ? 0.75 : 1 },
        }}
      >
        <Typography sx={{
          fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em",
          textTransform: "uppercase", color: textSecondary, flex: 1,
        }}>
          {title}{count != null ? ` (${count})` : ""}
        </Typography>
        {/* A shut drawer that says only "History (14)" makes someone open it
            to find out whether anything has happened lately. The last thing
            in it is one line, so it goes on the outside. */}
        {subtitle && !open && (
          <Typography noWrap sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, maxWidth: "55%" }}>
            {subtitle}
          </Typography>
        )}
        <ExpandMoreIcon sx={{
          fontSize: 17, color: textMuted,
          transform: open ? "rotate(180deg)" : "none", transition: `transform 0.2s ${easing}`,
        }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: quiet ? 0.25 : 1.75, pb: 1.75 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

const actionBtn = (primary) => ({
  textTransform: "none",
  fontFamily: font,
  fontSize: "12px",
  fontWeight: 700,
  borderRadius: "999px",
  px: 1.5,
  py: 0.4,
  minHeight: 0,
  whiteSpace: "nowrap",
  ...(primary
    ? { backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#a81828" } }
    : { color: textPrimary, border: `1px solid ${border}`, backgroundColor: "#fff", "&:hover": { backgroundColor: "#F5F2EF" } }),
});

export default function StudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // One-line summaries for the Dig in rows, from the coaching call.
  const [digest, setDigest] = useState(null);
  // Bumped after the instructor acts, so the briefing re-reads straight away.
  const [briefingTick, setBriefingTick] = useState(0);
  const [record, setRecord] = useState(null);
  const [recordError, setRecordError] = useState(null);
  const [student, setStudent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const [panel, setPanel] = useState({ open: false, mode: "propose" });
  // Setting work goes to the middle of the screen, not the right-hand drawer:
  // it is a list being built rather than one field being filled, and the drawer
  // could only ever produce a sentence nobody could check afterwards. Declared
  // up here with the rest of the state — below the `if (!record)` return it
  // would be a conditional hook.
  const [homework, setHomework] = useState({ open: false, prefill: null });

  const loadRecord = useCallback(async () => {
    try {
      const r = await get_student_record_api(studentId);
      if (r.code === 200) { setRecord(r); setRecordError(null); }
      else setRecordError(r.message || "Could not load this student's record");
    } catch {
      setRecordError("Could not load this student's record");
    }
  }, [studentId]);

  const loadProfile = useCallback(async () => {
    try {
      const result = await get_school_student_detail_api(studentId);
      const data = result.code === 200 ? (result.student || result.data || result) : (result._id ? result : null);
      if (data) setStudent(data);
    } catch {
      // The record is the page. A failed profile fetch loses the edit form,
      // not the reason anyone opened this.
    }
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    // Settled, not all: the record and the editable profile are independent,
    // and one failing must not blank the other. Note invokeApi never rejects —
    // both helpers below check the response code themselves.
    Promise.allSettled([loadRecord(), loadProfile()]).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [loadRecord, loadProfile]);

  useEffect(() => {
    if (!student) return;
    setEditData({
      first_name: student.first_name || "",
      last_name: student.last_name || "",
      email: student.email || "",
      phone: student.phone || "",
      status: student.status || "active",
    });
    if (student.cachedAIAnalysis?.briefing) setAiSummary(student.cachedAIAnalysis.briefing);
  }, [student]);

  const handleGenerateAI = async (force = false) => {
    setAiLoading(true);
    try {
      const result = await generate_student_mistake_briefing_api(studentId, { subject: null, forceRegenerate: force });
      if (result.code === 200 && result.briefing) setAiSummary(result.briefing);
      else enqueueSnackbar(result.message || "Could not generate a briefing", { variant: "error" });
    } catch {
      enqueueSnackbar("Could not generate a briefing", { variant: "error" });
    } finally {
      setAiLoading(false);
    }
  };

  const taskAction = async (taskId, action) => {
    setBusy(true);
    try {
      const r = await update_student_task_api(studentId, taskId, action);
      if (r.code === 200) { await loadRecord(); enqueueSnackbar(action === "approve" ? "Approved — the student can see it now" : "Updated", { variant: "success" }); }
      else enqueueSnackbar(r.message || "That didn't go through", { variant: "error" });
    } catch {
      enqueueSnackbar("That didn't go through", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const approveSummary = async (bookingId) => {
    setBusy(true);
    try {
      const r = await approve_session_summary_api(studentId, bookingId);
      if (r.code === 200) { await loadRecord(); enqueueSnackbar("Summary published to the student", { variant: "success" }); }
      else enqueueSnackbar(r.message || "That didn't go through", { variant: "error" });
    } catch {
      enqueueSnackbar("That didn't go through", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!editData.first_name?.trim() || !editData.last_name?.trim()) {
      enqueueSnackbar("Name is required", { variant: "error" });
      return;
    }
    setIsSaving(true);
    try {
      const result = await update_school_student_api(studentId, editData);
      if (result.code === 200 || result.student) {
        enqueueSnackbar("Student updated", { variant: "success" });
        setIsEditing(false);
        loadProfile();
        loadRecord();
      } else enqueueSnackbar(result.message || "Failed to update", { variant: "error" });
    } catch {
      enqueueSnackbar("Failed to update", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendPasswordReset = async () => {
    setIsSendingReset(true);
    try {
      const result = await send_student_password_reset_api(studentId);
      if (result.code === 200 || result.success) enqueueSnackbar("Password reset email sent", { variant: "success" });
      else enqueueSnackbar(result.message || "Failed", { variant: "error" });
    } catch {
      enqueueSnackbar("Failed to send", { variant: "error" });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleResendInvite = async () => {
    setIsResendingInvite(true);
    try {
      const result = await resend_student_invite_api(studentId);
      if (result.code === 200 || result.success) enqueueSnackbar("Invite email resent", { variant: "success" });
      else enqueueSnackbar(result.message || "Failed to resend invite", { variant: "error" });
    } catch {
      enqueueSnackbar("Failed to resend invite", { variant: "error" });
    } finally {
      setIsResendingInvite(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await delete_school_student_api(studentId);
      if (result.code === 200 || result.success) {
        enqueueSnackbar("Student removed", { variant: "success" });
        navigate("/school/students");
      } else enqueueSnackbar(result.message || "Failed", { variant: "error" });
    } catch {
      enqueueSnackbar("Failed to remove", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  if (!record) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography sx={{ color: errorColor, fontFamily: font, mb: 2 }}>{recordError || "Student not found"}</Typography>
        <Button onClick={() => navigate("/school/students")} sx={secondaryButtonSx}>Back to Students</Button>
      </Box>
    );
  }

  const studentName = record.student.name;

  // Contact, edit form, account actions and the danger zone. All real, none of
  // them the reason anyone opened this — so they live collapsed at the bottom
  // of the left rail rather than owning the bottom half of the page.
  const profileBlock = (
    <Box>
      {isEditing ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <TextField label="First name" size="small" value={editData.first_name || ""}
            onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} sx={inputSx} />
          <TextField label="Last name" size="small" value={editData.last_name || ""}
            onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} sx={inputSx} />
          <TextField label="Email" size="small" value={editData.email || ""}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })} sx={inputSx} />
          <TextField label="Phone" size="small" value={editData.phone || ""}
            onChange={(e) => setEditData({ ...editData, phone: e.target.value })} sx={inputSx} />
          <FormControl size="small" sx={inputSx}>
            <Select value={editData.status || "active"} onChange={(e) => setEditData({ ...editData, status: e.target.value })}>
              {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleSave} disabled={isSaving} startIcon={<SaveIcon sx={{ fontSize: 15 }} />} sx={{ ...primaryButtonSx, minHeight: "34px", py: 0.5, px: 1.5, fontSize: "12px" }}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={() => setIsEditing(false)} startIcon={<CancelIcon sx={{ fontSize: 15 }} />} sx={{ ...secondaryButtonSx, minHeight: "34px", py: 0.5, px: 1.5, fontSize: "12px" }}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box sx={fieldRowSx}>
            <EmailIcon sx={{ fontSize: 15, color: textMuted }} />
            <Typography sx={{ fontSize: "12.5px", color: textPrimary, fontFamily: font, overflow: "hidden", textOverflow: "ellipsis" }}>
              {record.student.email || "—"}
            </Typography>
          </Box>
          <Box sx={fieldRowSx}>
            <PhoneIcon sx={{ fontSize: 15, color: textMuted }} />
            <Typography sx={{ fontSize: "12.5px", color: textPrimary, fontFamily: font }}>{student?.phone || "—"}</Typography>
          </Box>
          <Box sx={fieldRowSx}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: textMuted }} />
            <Typography sx={{ fontSize: "12.5px", color: textPrimary, fontFamily: font }}>
              Joined {record.student.enrolledAt ? moment(record.student.enrolledAt).format("D MMM YYYY") : "—"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25, pt: 1.25, borderTop: `1px solid ${border}` }}>
            <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => setIsEditing(true)} sx={actionBtn(false)}>
              Edit
            </Button>
            {student?.status === "pending" && (
              <Button size="small" onClick={handleResendInvite} disabled={isResendingInvite} startIcon={<SendIcon sx={{ fontSize: 14 }} />} sx={actionBtn(false)}>
                {isResendingInvite ? "Sending…" : "Resend invite"}
              </Button>
            )}
            <Button size="small" onClick={handleSendPasswordReset} disabled={isSendingReset} startIcon={<LockResetIcon sx={{ fontSize: 14 }} />} sx={actionBtn(false)}>
              {isSendingReset ? "Sending…" : "Password reset"}
            </Button>
          </Box>

          <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${border}` }}>
            {showDeleteConfirm ? (
              <Box>
                <Typography sx={{ color: textSecondary, fontSize: "12px", fontFamily: font, mb: 1, lineHeight: 1.45 }}>
                  Remove {studentName} from your school? This can't be undone.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" onClick={handleDelete} disabled={isDeleting} sx={{
                    backgroundColor: errorColor, color: "#fff", textTransform: "none", fontFamily: font,
                    fontWeight: 700, fontSize: "12px", borderRadius: "999px", px: 1.5,
                    "&:hover": { backgroundColor: "#b91c1c" },
                  }}>
                    {isDeleting ? "Removing…" : "Yes, remove"}
                  </Button>
                  <Button size="small" onClick={() => setShowDeleteConfirm(false)} sx={actionBtn(false)}>Cancel</Button>
                </Box>
              </Box>
            ) : (
              <Button size="small" onClick={() => setShowDeleteConfirm(true)} startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} sx={{
                color: errorColor, textTransform: "none", fontFamily: font, fontWeight: 600, fontSize: "12px",
                "&:hover": { backgroundColor: `${errorColor}10` },
              }}>
                Remove student
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  const openPanel = (mode, prefill = null) => {
    if (mode === "task") { setHomework({ open: true, prefill }); return; }
    setPanel({ open: true, mode, prefill });
  };

  // A time offered and unanswered belongs in this count. It was missing, so a
  // proposal an instructor had just made showed nowhere on the record they
  // made it from — only in the timeline, indistinguishable from a confirmed
  // session.
  const openProposals = record?.openItems?.proposals?.length || 0;
  const openCount =
    (record?.openItems?.tasks?.length || 0)
    + (record?.openItems?.awaitingApproval?.length || 0)
    + openProposals;
  // The newest thing in the history, said on the outside of the shut drawer.
  const newest = (record?.timeline || [])[0];
  const lastEvent = newest?.at
    ? `Last: ${kindMeta(newest.kind).label.toLowerCase()} · ${moment(newest.at).fromNow()}`
    : null;

  /**
   * The brief's "Cover this" carries its own evidence into the composer.
   *
   * The four header buttons all pre-filled from `record.weakest[0]` — one
   * subject, one canned sentence — which is why every draft read like a mail
   * merge. The action was separated from its reason, so the only reason
   * available was the page-level average. An observation IS the argument for
   * the action, so the button next to it carries that observation.
   */
  const coverTopic = (item) => {
    const first = (record?.student?.name || "").split(" ")[0] || "there";
    const thing = item.topic.replace(/-/g, " ");
    const quote = item.patterns?.[0];

    // WHAT MAKES THIS READ AS AN INSTRUCTOR AND NOT A CRM
    // It names the actual mistake instead of quoting a score the student can
    // already see; it is first person, so a human plainly looked; and it
    // DOWNGRADES the problem — "quick one to fix" — because a CRM inflates a
    // problem to justify an offer and a teacher deflates it to keep the
    // student in the room. No percentages: those are for the instructor.
    const message = quote
      ? `Hi ${first} — I was going over your ${item.subjectLabel.toLowerCase()} and the ${thing} questions keep catching you. `
        + `You've gone for "${quote.answer}" a few times there. It's a quick one to fix once you see it — `
        + `shall we do that at the start of the next lesson?`
      : `Hi ${first} — I had a look at your ${item.subjectLabel.toLowerCase()} and ${thing} is the one that keeps tripping you up. `
        + `Nothing dramatic, but it's worth twenty minutes together rather than another pass on your own. `
        + `Want to start there next time?`;

    openPanel("message", { message, subject: item.subject, topic: item.topic });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, margin: "0 auto" }}>
      {/* One target, not an icon button beside some text that looks like a
          link and is not one. The words are the bigger thing to aim at, so
          they were the half more likely to be clicked. */}
      <Box
        component="button"
        type="button"
        onClick={() => navigate("/school/students")}
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1,
          background: "none", border: "none", padding: "4px 8px 4px 4px", marginLeft: "-4px",
          borderRadius: "6px", cursor: "pointer", color: textSecondary, fontFamily: font,
          transition: `background 0.12s ${easing}, color 0.12s ${easing}`,
          "&:hover": { backgroundColor: "#F5F2EF", color: textPrimary },
          "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography component="span" sx={{ fontFamily: font, fontSize: "12px", color: "inherit" }}>
          All students
        </Typography>
      </Box>

      <RecordHeader
        record={record}
        aiSummary={aiSummary}
        aiLoading={aiLoading}
        onGenerateAI={handleGenerateAI}
        showBriefing={(record.focus?.basedOn || 0) === 0}
        actions={
          <>
            {/* Modules decide what you bought, roles decide which of it you
                see. Proposing a session is the Tutors module; a note and a
                task are how any school runs itself. */}
            {record.modules?.tutors !== false && (
              <Button size="small" sx={actionBtn(true)} onClick={() => openPanel("propose")}>Propose session</Button>
            )}
            <Button size="small" sx={actionBtn(false)} onClick={() => openPanel("note")}>Add note</Button>
            <Button size="small" sx={actionBtn(false)} onClick={() => openPanel("task")}>Assign task</Button>
            {record.modules?.insights !== false && (
              <Button size="small" sx={actionBtn(false)} onClick={() => openPanel("flag")}>Flag weak area</Button>
            )}
          </>
        }
      />

      {/* Above the grid, full width, directly under the identity strip — the
          position the risk band and the revenue figure used to hold between
          them. Which of the two shows depends on whether there is anything to
          say yet: a student with no answers gets three things to DO, not a
          ranking of nothing. */}
      {/* ONE LINE, NOT A RANKED LIST.
          The brief that used to sit here was the biggest block on the page and
          it never changed between visits: the same three topics, each with its
          counts, its quote and its own button. An instructor with three
          minutes takes ONE thing into the room, and a student who is doing
          fine deserves an opening line rather than an empty box. The full
          ranking is not lost — every topic is one click into its paper below. */}
      {(record.focus?.basedOn || 0) === 0 ? (
        <RecordGetStarted
          record={record}
          studentId={studentId}
          onPropose={() => openPanel("propose")}
          onSetWork={() => openPanel("task")}
          onReload={loadRecord}
        />
      ) : (
        // What to do next, with evidence and a button that does it. Replaces
        // the single "worth raising" line, which named a topic and stopped.
        <Box sx={{ maxWidth: 1100 }}>
        <RecordBriefing
          studentId={studentId}
          openItems={record.openItems}
          studentName={record.student?.name}
          onLoaded={(d) => setDigest(d?.digest || null)}
          onPropose={() => openPanel("propose")}
          refreshKey={briefingTick}
          onDismiss={async (key) => { await act_on_coaching_api(studentId, key, "dismiss"); setBriefingTick((t) => t + 1); }}
          onAct={(action, cta) => {
            // The note is marked acted only once the booking/task/message is
            // actually sent — see the panels' onDone below.
            const coach = { coachKey: action.id, coachHow: cta.kind };
            if (cta.kind === "propose") openPanel("propose", { subject: action.subject, note: cta.note, ...coach });
            else if (cta.kind === "task") openPanel("task", { items: cta.items, ...coach });
            else if (cta.kind === "message") openPanel("message", { message: cta.text, ...coach });
          }}
        />
        </Box>
      )}

      {/* EVERY SUBJECT, straight under the briefing: one card per paper, open
          a paper for its topics, a topic for the questions and answers. */}
      <Box sx={{ maxWidth: 1100, mt: 2.5 }}>
        <RecordSubjects
          coverage={record.coverage}
          lessons={record.lessons}
          studentId={studentId}
          onSetWork={(t) => openPanel("task", { items: [{ kind: "questions", subject: t.subject, topic: t.topic === "all" ? "" : t.topic, count: 20 }] })}
          onMessage={(t) => coverTopic({ ...t, subjectLabel: t.subjectLabel || t.subject })}
        />
      </Box>

      {/* MORE, shut until wanted. */}
      <Box sx={{ maxWidth: 1100, mt: 2.5 }}>
        <DigIn title="Activity" summary={digest?.activity}>
          <RecordActivity studentId={studentId} studentName={record.student?.name} embedded />
        </DigIn>
        {record.modules?.insights !== false && (
          <DigIn title={record.modules?.adaptPlus ? "Exam picture · Adapt+" : "Exam picture"} summary={record.modules?.adaptPlus ? digest?.exam : "Adapt+ is not on your plan."}>
            <RecordExamPicture studentId={studentId} studentName={record.student?.name} adaptPlus={record.modules?.adaptPlus === true} embedded />
          </DigIn>
        )}
        <DigIn title="Set work" summary={digest?.work}>
          <RecordWork record={record} studentName={(record.student?.name || "").split(" ")[0]} onSetWork={() => openPanel("task")} />
        </DigIn>
        {record.modules?.insights !== false && (
          <DigIn title={`Ask about ${(record.student?.name || "").split(" ")[0] || "this student"}`} summary="Ask anything in plain words — answered from this record.">
            <StudentAIChat studentId={studentId} studentName={record.student?.name} compact />
          </DigIn>
        )}
      </Box>

      {/* THE ARCHIVE, ON ITS OWN GROUND.
          Everything above this line is something to read or act on today.
          What is below it is the record's memory — real, occasionally needed,
          and not what anyone opened the page for. It stops being white cards
          and becomes rules on the page's own background, so the change of
          register is visible before a word is read and nobody mistakes it for
          more of the same. */}
      <Box sx={{
        maxWidth: 1100, mt: 3, pt: 0.5,
        borderTop: `2px solid ${border}`,
      }}>
        <Typography sx={{
          fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em",
          textTransform: "uppercase", color: textMuted, mt: 1.5, mb: 0.5,
        }}>
          The rest of the record
        </Typography>

        <Drawer tone="quiet" title="History" count={(record.timeline || []).length} subtitle={lastEvent}>
          <RecordTimeline timeline={record.timeline || []} studentId={studentId} onApproveSummary={approveSummary} />
        </Drawer>

        <Drawer tone="quiet" title="Contact & account">
          {profileBlock}
        </Drawer>

        {/* "Work set" above is the read surface — what was set and how far
            through it they are. This is the action surface: approving an
            assistant's draft, dismissing something, closing a task by hand.
            Kept quiet because it is rarely the reason anyone came. */}
        {(
          // Quiet unless something is waiting on the student to answer — that
          // one IS the reason someone came back to this record, and it is on a
          // clock, so it must not sit behind a collapsed heading.
          <Drawer
            tone={openProposals > 0 ? undefined : "quiet"}
            title={openProposals > 0 ? "Waiting on them, approvals & tasks" : "Approvals & task actions"}
            count={openCount}
            defaultOpen={false}
          >
            <RecordOpenItems
              record={record} busy={busy}
              onApproveTask={(id) => taskAction(id, "approve")}
              onDismissTask={(id) => taskAction(id, "dismiss")}
              onCompleteTask={(id) => taskAction(id, "done")}
              onApproveSummary={approveSummary}
            />
          </Drawer>
        )}
      </Box>

      <HomeworkModal
        open={homework.open}
        record={record}
        studentId={studentId}
        prefill={homework.prefill}
        onClose={() => setHomework({ open: false, prefill: null })}
        onDone={async () => {
          if (homework.prefill?.coachKey) await act_on_coaching_api(studentId, homework.prefill.coachKey, "task").catch(() => {});
          setBriefingTick((t) => t + 1);
          loadRecord();
        }}
      />

      <RecordActionPanel
        open={panel.open}
        mode={panel.mode}
        record={record}
        studentId={studentId}
        prefill={panel.prefill || null}
        onClose={() => setPanel({ ...panel, open: false })}
        onDone={async () => {
          if (panel.prefill?.coachKey) await act_on_coaching_api(studentId, panel.prefill.coachKey, panel.prefill.coachHow).catch(() => {});
          setBriefingTick((t) => t + 1);
          loadRecord();
        }}
      />
    </Box>
  );
}
